import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { STATUS_CONFIG, type Shipment } from '../../mocks/shipments';
import { fetchShipmentById } from '../../lib/shipmentsService';
import QRScannerModal from '../../components/feature/QRScannerModal';

export default function TrackingPage() {
  const [searchParams] = useSearchParams();
  const initialQ = searchParams.get('q') || '';
  const [query, setQuery] = useState(initialQ);
  const [submitted, setSubmitted] = useState(!!initialQ);
  const [showScanner, setShowScanner] = useState(false);
  const [shipment, setShipment] = useState<Shipment | null>(null);
  const [searching, setSearching] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    if (!submitted || !query.trim()) { setShipment(null); return; }
    const id = query.trim().toUpperCase();
    setSearching(true);
    fetchShipmentById(id).then((dbResult) => {
      setShipment(dbResult ?? null);
      setSearching(false);
    });
  }, [submitted, query]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = query.trim();
    if (!trimmed) return;
    setSubmitted(true);
    navigate(`/track?q=${encodeURIComponent(trimmed)}`, { replace: true });
  };

  const handleScanResult = (decoded: string) => {
    setShowScanner(false);
    const cleaned = decoded.trim();
    setQuery(cleaned);
    setSubmitted(true);
    navigate(`/track?q=${encodeURIComponent(cleaned)}`, { replace: true });
  };

  const progress: Record<string, number> = {
    pending: 5,
    hold: 15,
    on_hold: 15,
    picked_up: 25,
    in_transit: 55,
    out_for_delivery: 82,
    delivered: 100,
  };

  const progressColor: Record<string, string> = {
    pending: '#f59e0b',
    hold: '#ef4444',
    on_hold: '#ef4444',
    picked_up: '#f59e0b',
    in_transit: 'var(--primary)',
    out_for_delivery: '#22c55e',
    delivered: '#16a34a',
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <div
        className="pt-28 pb-16 text-white"
        style={{ background: 'linear-gradient(135deg, #0d2b2b 0%, var(--primary) 100%)' }}
      >
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h1 className="font-heading font-bold text-4xl md:text-5xl mb-4">
            {t('track.title')}
          </h1>
          <p className="text-white/70 text-lg mb-10">
            {t('track.subtitle')}
          </p>

          <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 max-w-2xl mx-auto">
            <div className="flex-1 flex items-center gap-3 bg-white rounded-xl px-4 py-3.5">
              <i className="ri-barcode-box-line text-gray-400 text-lg flex-shrink-0" />
              <input
                type="text"
                value={query}
                onChange={(e) => { setQuery(e.target.value); setSubmitted(false); }}
                placeholder={t('track.placeholder')}
                className="flex-1 bg-transparent text-gray-800 text-sm outline-none placeholder-gray-400 min-w-0 font-mono"
              />
              {query && (
                <button
                  type="button"
                  onClick={() => { setQuery(''); setSubmitted(false); }}
                  className="text-gray-400 hover:text-gray-600 cursor-pointer flex-shrink-0"
                >
                  <i className="ri-close-line" />
                </button>
              )}
            </div>

            <button
              type="button"
              onClick={() => setShowScanner(true)}
              className="px-5 py-3.5 rounded-xl text-sm font-semibold text-white border border-white/30 hover:bg-white/10 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2 justify-center"
            >
              <i className="ri-qr-scan-2-line text-lg" />
              {t('track.scanBtn')}
            </button>

            <button
              type="submit"
              className="px-8 py-3.5 rounded-xl text-sm font-semibold text-white whitespace-nowrap transition-opacity hover:opacity-90 cursor-pointer"
              style={{ background: '#0d2b2b' }}
            >
              {t('track.trackBtn')}
            </button>
          </form>

          <p className="text-white/40 text-xs mt-4">
            <i className="ri-lock-line mr-1" />
            {t('track.privacy')}
          </p>
        </div>
      </div>

      {/* Results */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        {/* Loading */}
        {submitted && searching && (
          <div className="text-center py-20">
            <div
              className="w-12 h-12 border-2 border-t-transparent rounded-full animate-spin mx-auto mb-5"
              style={{ borderColor: 'var(--primary)', borderTopColor: 'transparent' }}
            />
            <p className="text-gray-400 text-sm">Looking up your shipment...</p>
          </div>
        )}

        {/* Not yet searched */}
        {!submitted && (
          <div className="text-center py-20">
            <div
              className="w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6"
              style={{ background: 'var(--primary-light)' }}
            >
              <i className="ri-map-pin-time-line text-4xl" style={{ color: 'var(--primary)' }} />
            </div>
            <h3 className="font-heading font-semibold text-2xl text-gray-800 mb-3">
              {t('track.enter.title')}
            </h3>
            <p className="text-gray-500 text-sm max-w-sm mx-auto leading-relaxed">
              {t('track.enter.desc')} <span className="font-mono font-semibold text-gray-700">SWT-2024-XXXXXX</span>.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-4">
              {[
                { icon: 'ri-time-line', key: 'track.feature1' },
                { icon: 'ri-map-2-line', key: 'track.feature2' },
                { icon: 'ri-notification-4-line', key: 'track.feature3' },
              ].map(({ icon, key }) => (
                <div key={key} className="flex items-center gap-2 text-sm text-gray-500 bg-gray-50 rounded-xl px-4 py-2.5 border border-gray-100">
                  <i className={`${icon} text-base`} style={{ color: 'var(--primary)' }} />
                  <span>{t(key)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* No result */}
        {submitted && !searching && !shipment && (
          <div className="text-center py-20">
            <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-5 border border-red-100">
              <i className="ri-search-eye-line text-2xl text-red-400" />
            </div>
            <h3 className="font-heading font-semibold text-xl text-gray-700 mb-2">
              {t('track.notFound.title')}
            </h3>
            <p className="text-gray-500 text-sm mb-2">
              {t('track.notFound.desc')}{' '}
              <span className="font-mono font-semibold text-gray-800">{query}</span>.
            </p>
            <p className="text-gray-400 text-xs mb-8">
              {t('track.notFound.hint')}{' '}
              <Link to="/contact" className="underline" style={{ color: 'var(--primary)' }}>
                {t('track.notFound.support')}
              </Link>
              .
            </p>
          </div>
        )}

        {/* Found */}
        {submitted && !searching && shipment && (() => {
          const cfg = STATUS_CONFIG[shipment.status] ?? STATUS_CONFIG['in_transit'];
          const pct = progress[shipment.status] ?? 5;
          return (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-heading font-semibold text-xl text-gray-900">{t('track.found.title')}</h2>
                <span className="text-xs text-gray-400">
                  <i className="ri-lock-fill mr-1" />
                  {t('track.found.private')}
                </span>
              </div>

              <Link
                to={`/track/${shipment.id}`}
                className="block bg-white border border-gray-100 rounded-2xl p-6 hover:border-transparent transition-all group cursor-pointer"
                style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.boxShadow = '0 8px 30px rgba(13,148,136,0.12)'; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.boxShadow = '0 1px 4px rgba(0,0,0,0.05)'; }}
              >
                <div className="flex flex-wrap items-start justify-between gap-4 mb-5">
                  <div>
                    <div className="flex items-center gap-3 mb-1">
                      <span className="font-mono text-sm font-bold text-gray-900">{shipment.id}</span>
                      <span
                        className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border ${cfg.bg} ${cfg.color} ${cfg.blinkClass}`}
                      >
                        {(['hold', 'out_for_delivery', 'picked_up'] as string[]).includes(shipment.status) && (
                          <span className="w-1.5 h-1.5 rounded-full bg-current animate-ping flex-shrink-0" />
                        )}
                        <i className={cfg.icon} />
                        {cfg.label}
                      </span>
                    </div>
                    <div className="text-gray-500 text-sm">{shipment.description} · {shipment.weight} kg · {shipment.service}</div>
                    {shipment.note && (
                      <div className="mt-1.5 flex items-start gap-1.5 text-xs text-amber-700 bg-amber-50 border border-amber-200/60 rounded-lg px-2.5 py-1.5 max-w-xs">
                        <i className="ri-information-line flex-shrink-0 mt-0.5" />
                        <span>{shipment.note}</span>
                      </div>
                    )}
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-gray-400 mb-1">{t('track.found.estDelivery')}</div>
                    <div className="font-semibold text-gray-900 text-sm">{shipment.estimatedDelivery}</div>
                  </div>
                </div>

                {/* Route */}
                <div className="flex items-center gap-3 mb-5">
                  <div className="text-center">
                    <div className="font-semibold text-gray-900 text-sm">{shipment.origin.city}</div>
                    <div className="text-gray-400 text-xs">{shipment.sender.name}</div>
                  </div>
                  <div className="flex-1 flex items-center gap-1">
                    <div className="flex-1 h-px bg-gray-200" />
                    <div
                      className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ background: 'var(--primary-light)' }}
                    >
                      <i className="ri-arrow-right-s-line text-xs" style={{ color: 'var(--primary)' }} />
                    </div>
                    <div className="flex-1 h-px bg-gray-200" />
                  </div>
                  <div className="text-center">
                    <div className="font-semibold text-gray-900 text-sm">{shipment.destination.city}</div>
                    <div className="text-gray-400 text-xs">{shipment.receiver.name}</div>
                  </div>
                </div>

                {/* Progress */}
                <div>
                  <div className="flex justify-between text-xs text-gray-400 mb-2">
                    <span>{t('track.found.progress')}</span>
                    <span>{pct}%</span>
                  </div>
                  <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all"
                      style={{ width: `${pct}%`, background: progressColor[shipment.status] }}
                    />
                  </div>
                </div>

                <div className="mt-4 flex items-center justify-end gap-1.5 text-xs font-semibold" style={{ color: 'var(--primary)' }}>
                  {t('track.found.viewDetails')}
                  <i className="ri-arrow-right-line" />
                </div>
              </Link>
            </div>
          );
        })()}
      </div>

      {showScanner && (
        <QRScannerModal
          onResult={handleScanResult}
          onClose={() => setShowScanner(false)}
        />
      )}

      <Footer />
    </div>
  );
}
